// @greenlight-spec: 20-agent-plugins.md#marketplace-repo-bootstrap
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: decisions/0040-multiple-plugin-marketplace-repositories.md

import * as React from 'react';

import { MarketplaceEditor } from '@/components/greenlight/marketplace/MarketplaceEditor';
import {
  directCloneAgents,
  MarketplaceVisibilityGuide,
} from '@/components/greenlight/marketplace/MarketplaceVisibilityGuide';
import type { BootstrapMarketplace } from '@/components/greenlight/marketplace/useMarketplaceEditor';
import { StepHeader } from '@/components/install/StepHeader';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { mergeRepository } from '@/lib/marketplace';
import {
  PluginMarketplaceStatusSchema,
  type AgentKind,
  type InstallationState,
  type PluginMarketplaceBootstrapResult,
  type PluginMarketplaceStatus,
} from '@greenlight/shared';
import type { InstallScmInstallation } from '@greenlight/contract';

interface PluginMarketplaceStepProps {
  installationState: InstallationState;
  /** Live SCM connection — supplies the org prefix on the create form. */
  scmInstallation: InstallScmInstallation | null;
  blocked: boolean;
  /** Names the agents that still clone from GitHub; the org record already knows it. */
  supportedAgents: readonly AgentKind[];
  onAdvance: () => void;
  isSubmitting: boolean;
}

function persistedRepositories(installationState: InstallationState): PluginMarketplaceStatus[] {
  const details = installationState.steps?.plugin_marketplace?.details;
  const parsed = PluginMarketplaceStatusSchema.array().safeParse(details?.['repositories']);
  return parsed.success ? parsed.data : [];
}

/** Installs completed before multi-repo support persisted `{ repo_full_name }` only. */
function legacyRepoFullName(installationState: InstallationState): string | null {
  const details = installationState.steps?.plugin_marketplace?.details;
  const value = details?.['repo_full_name'];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

export function PluginMarketplaceStep({
  installationState,
  scmInstallation,
  blocked,
  supportedAgents,
  onAdvance,
  isSubmitting,
}: PluginMarketplaceStepProps): React.JSX.Element {
  const persisted = persistedRepositories(installationState);
  const [repositories, setRepositories] = React.useState(persisted);
  const bootstrapMutation = api.install.bootstrapPluginMarketplace.useMutation();
  const stepComplete = installationState.steps?.plugin_marketplace?.status === 'complete';
  const legacyRepo = repositories.length === 0 ? legacyRepoFullName(installationState) : null;
  const connected = scmInstallation !== null && !scmInstallation.suspended;
  // Once a repository exists, moving on is the primary action; creating another is secondary.
  const continueOffered = repositories.length > 0 || stepComplete;

  React.useEffect(() => setRepositories(persisted), [installationState]);

  const handleBootstrap: BootstrapMarketplace = async (
    body,
  ): Promise<PluginMarketplaceBootstrapResult> => {
    const result = await bootstrapMutation.mutateAsync({ body });
    if (result.status !== 200) throw result;
    setRepositories((current) => mergeRepository(current, result.body.marketplace));
    return result.body;
  };

  return (
    <div className="space-y-6" data-testid="step-plugin_marketplace">
      <StepHeader step="plugin_marketplace" title="Plugin marketplaces">
        Create a private GitHub repository containing the Greenlight plugin. A public one is an
        optional fallback.
      </StepHeader>

      {blocked ? (
        <div
          className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground"
          data-testid="step-blocked"
        >
          This step is blocked until the GitHub App step is complete.
        </div>
      ) : (
        <>
          {legacyRepo && (
            <div
              className="rounded-md border border-dashed px-4 py-3 text-sm text-muted-foreground"
              data-testid="marketplace-legacy-repo"
            >
              <span className="font-mono text-foreground">{legacyRepo}</span> already exists for
              this org. Continue, or create additional repositories below.
            </div>
          )}
          <MarketplaceVisibilityGuide />
          <MarketplaceEditor
            directCloneAgents={directCloneAgents(supportedAgents)}
            repositories={repositories}
            githubAppReady={connected}
            orgHandle={connected ? scmInstallation.orgHandle : null}
            recoveryContext="wizard"
            createVariant={continueOffered ? 'outline' : 'default'}
            onBootstrap={handleBootstrap}
          />
          {continueOffered && (
            <div className="flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-muted-foreground">
                You can create more repositories now or continue and manage them later in Settings.
              </p>
              <Button
                type="button"
                className="h-11 w-full md:h-9 sm:w-auto sm:shrink-0"
                onClick={onAdvance}
                disabled={isSubmitting || bootstrapMutation.isPending}
                data-testid="step-next"
              >
                {isSubmitting ? 'Saving…' : 'Continue'}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
